import { Component, h, Host, Prop, State } from '@stencil/core'
import Axios from 'axios'
import { debounce } from 'lodash-es'
import { AppSharedPropsStore } from 'src/app'
import { flashToast } from 'src/app/toast'
import { asyncToast } from 'src/app/toast'
import { ICallSharing, IPageCallDetailFeatures } from 'src/call/types'
import { CallShareableTypes } from 'src/share/types'
import { canAccess, getUniversalShareUrl } from 'src/share/universal'
import { copyText } from 'src/utils/clipboard'

import { CallMenuButton } from '../notes/ui'
import { CallStore, getCallShareDisabledReason } from '../state'

@Component({
  tag: 'page-call-detail-share-menu',
})
export class ShareMenu {
  @Prop({ mutable: true }) sharing: ICallSharing
  @Prop() features: IPageCallDetailFeatures

  @State() selectedLinkType: CallShareableTypes
  @State() showShareDialog = false

  fetchShareLink = debounce(
    async () => {
      const maybeError = this.universalShareable.shareUrl ? 'Error copying' : 'Error generating share link'
      const activatePromise = getUniversalShareUrl(this.universalShareable).then((url) => ({
        plain_text: url,
      }))
      const copyPromise = copyText(activatePromise, true)
      asyncToast(copyPromise, 'Copying...', 'Copied', maybeError)
      return copyPromise
    },
    1500,
    { leading: true, trailing: false }
  )

  get universalShareable() {
    if (this.features.call_page_share_modal) {
      return this.sharing?.universalShareable
    }
    return CallStore.state.call.universalShareable
  }

  universalClickButton = () => {
    if (this.features.call_page_share_modal) {
      this.showShareDialog = true
    } else {
      this.fetchShareLink()
    }
  }

  render() {
    const shareUnavailable = !canAccess(this.universalShareable)
    const shareDisabledByTeamAdmin =
      AppSharedPropsStore.state.currentUser?.team?.preferences?.share_links_enabled === false

    if (shareUnavailable && !shareDisabledByTeamAdmin) {
      return null
    }

    const callShareDisabledReason = getCallShareDisabledReason(this.universalShareable)

    return (
      <Host>
        <CallMenuButton disabled={shareUnavailable} class='w-full' onClick={() => this.universalClickButton()}>
          <span>Share</span>
          <ui-icon icon='link-45deg' class='text-xl opacity-50' />
          {callShareDisabledReason && <ui-tooltip anchor='bottom'>{callShareDisabledReason}</ui-tooltip>}
        </CallMenuButton>
        {this.showShareDialog && this.sharing && (
          <page-call-detail-share-dialog
            sharing={this.sharing}
            fetchShareLink={this.fetchShareLink}
            onCancel={() => (this.showShareDialog = false)}
            onAddedSharingPeople={async ({ detail: { emails, access, notifyOnCreate } }) => {
              try {
                const response = await Axios.post(this.sharing.actions.createTokenUrl, {
                  emails,
                  access,
                  notify_on_create: notifyOnCreate,
                  call_id: CallStore.state.call.id,
                })
                this.sharing = {
                  ...this.sharing,
                  people: this.sharing.people.concat(response.data),
                }
                flashToast({ type: 'success', text: 'Access updated' })
              } catch (error) {
                flashToast({ type: 'alert', text: 'Failed to grant access' })
              }
            }}
            onChangedPersonAccess={async ({ detail: { person, isRemoved } }) => {
              try {
                if (isRemoved && person.actions.deactivateUrl) {
                  // Remove Access:
                  await Axios.put(person.actions.deactivateUrl)
                  this.sharing = {
                    ...this.sharing,
                    people: this.sharing.people.filter((existingPerson) => existingPerson.email !== person.email),
                  }
                } else if (person.actions.updateAccessUrl) {
                  // Update Access:
                  await Axios.put(person.actions.updateAccessUrl, {
                    access: person.access,
                  })
                  this.sharing = {
                    ...this.sharing,
                    people: this.sharing.people.map((existingPerson) =>
                      existingPerson.email === person.email ? person : existingPerson
                    ),
                  }
                }
              } catch (error) {
                flashToast({ type: 'alert', text: 'Failed to update access' })
              }
            }}
          />
        )}
      </Host>
    )
  }
}
